import type { SxProps, Theme } from "@mui/material";

export const sourceCard = (active: boolean): SxProps<Theme> => ({
  borderRadius: 2,
  border: `2px solid ${active ? "#026937" : "#ededed"}`,
  boxShadow: active
    ? "0px 6px 20px rgba(0,0,0,0.1)"
    : "0px 4px 16px rgba(0,0,0,0.05)",
  transition: "transform 0.2s",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0px 6px 20px rgba(0,0,0,0.1)"
  },
  backgroundColor: active ? "primary.main" : "white"
});

/**
 * Estilos comunes para botones pill-shaped
 * Aplica estilos consistentes a todos los botones con forma de píldora
 */
export const pillButtonBase: SxProps<Theme> = {
  borderRadius: "50px",
  height: "44px",
  textTransform: "uppercase",
  fontWeight: "bold",
  fontSize: "14px",
  letterSpacing: 0,
  boxShadow: "none !important",
  "&:hover": {
    boxShadow: "none !important"
  },
  "&:focus": {
    boxShadow: "none !important"
  },
  "&:active": {
    boxShadow: "none !important"
  }
};

/**
 * Estilos para botones pill-shaped con fondo negro (contained)
 * @param hoverColor - Color de fondo en hover (opcional, por defecto mantiene el mismo color)
 */
export const pillButtonContained = (
  backgroundColor: string = "#000000",
  hoverColor?: string
): SxProps<Theme> => ({
  ...pillButtonBase,
  backgroundColor,
  color: "#FFFFFF",
  "&:hover": {
    ...pillButtonBase["&:hover"],
    backgroundColor: hoverColor || backgroundColor
  }
});

/**
 * Estilos para botones pill-shaped outlined
 * @param borderColor - Color del borde (opcional, por defecto negro)
 */
export const pillButtonOutlined = (
  borderColor: string = "#000000"
): SxProps<Theme> => ({
  ...pillButtonBase,
  borderColor,
  color: borderColor,
  "&:hover": {
    ...pillButtonBase["&:hover"],
    borderColor,
    backgroundColor: "transparent"
  }
});

/**
 * Estilos para píldoras de enlaces y fuentes (similar a SourcesButton)
 * Aplica estilos consistentes a enlaces y píldoras de fuentes
 */
export const linkPillStyles = (theme: Theme): SxProps<Theme> => ({
  height: "19px",
  borderRadius: "60px",
  bgcolor: theme.palette.customColors.chipBg,
  pt: "4px",
  pr: "8px",
  pb: "4px",
  pl: "8px",
  border: "none",
  cursor: "pointer",
  textTransform: "none",
  fontFamily: "Ringside Narrow",
  fontSize: "14px",
  fontWeight: 325,
  fontStyle: "normal",
  lineHeight: "14px",
  letterSpacing: "0%",
  color: "#888888",
  display: "inline-flex",
  alignItems: "center",
  textDecoration: "none",
  "&:hover": {
    bgcolor: theme.palette.customColors.hoverBackground,
    textDecoration: "none"
  }
});

/**
 * Estilos base para Typography común en ProfileDrawer
 * @param fontSize - Tamaño de fuente (por defecto "14px")
 * @param lineHeight - Altura de línea (por defecto "18px" para 14px, "22px" para 18px)
 * @param color - Color del texto (función que recibe theme o string)
 */
export const profileTypographyBase = (
  fontSize: string = "14px",
  lineHeight?: string,
  color?: string | ((theme: Theme) => string)
): SxProps<Theme> => ({
  fontFamily: (theme: Theme) => theme.typography.fontFamily,
  fontWeight: 325,
  fontSize,
  lineHeight: lineHeight || (fontSize === "18px" ? "22px" : "18px"),
  letterSpacing: "0%",
  ...(color && {
    color: typeof color === "function" ? color : color
  })
});

/**
 * Estilos para Typography de texto secundario (gris)
 */
export const profileTypographySecondary = (
  fontSize: string = "14px",
  lineHeight: string = "120%"
): SxProps<Theme> => ({
  ...profileTypographyBase(fontSize, lineHeight, "#888888"),
  textAlign: "center"
});

/**
 * Estilos para Typography de título de usuario
 */
export const profileTypographyTitle = (): SxProps<Theme> => ({
  ...profileTypographyBase(
    "18px",
    "22px",
    (theme: Theme) => theme.palette.customColors.textPrimary
  ),
  mb: 0.5,
  textAlign: "center"
});

/**
 * Estilos para Typography de email de usuario
 */
export const profileTypographyEmail = (): SxProps<Theme> => ({
  ...profileTypographyBase(
    "18px",
    "22px",
    (theme: Theme) => theme.palette.customColors.textDisabled
  ),
  textAlign: "center"
});

/**
 * Estilos para IconButton común en ProfileDrawer
 */
export const profileIconButton = (): SxProps<Theme> => ({
  color: (theme: Theme) => theme.palette.customColors.textPrimary,
  "&:hover": {
    backgroundColor: (theme: Theme) =>
      theme.palette.customColors.hoverBackground
  }
});

/**
 * Estilos para IconButton sin hover background
 */
export const profileIconButtonTransparent = (): SxProps<Theme> => ({
  color: (theme: Theme) => theme.palette.customColors.textPrimary,
  "&:hover": {
    backgroundColor: "transparent"
  }
});

/**
 * Estilos para enlaces del footer
 */
export const profileFooterLink = (): SxProps<Theme> => ({
  ...profileTypographySecondary("14px", "120%"),
  textDecoration: "none",
  background: "none",
  border: "none",
  cursor: "pointer",
  p: 0,
  m: 0
});

/**
 * Estilos para diálogo de logout
 */
export const profileDialogStyles = (): SxProps<Theme> => ({
  borderRadius: 4,
  p: { xs: 2.5, sm: 3 },
  pb: { xs: 3, sm: 3.5 }
});

/**
 * Estilos para título de diálogo
 */
export const profileDialogTitle = (): SxProps<Theme> => ({
  textAlign: "center",
  fontSize: 20,
  fontWeight: 600,
  pb: 2
});

/**
 * Estilos para mensaje de diálogo
 */
export const profileDialogMessage = (): SxProps<Theme> => ({
  fontSize: 16,
  lineHeight: "20px",
  fontWeight: 325,
  color: "#000000",
  textAlign: "center",
  mb: 3
});
